import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import {
  PostHogCaptureProperties, PostHogEventNames,
  PostHogProvider
} from 'Douleutaras/app/lib/posthog/posthog.provider';
import { isDevelopment, isRealProduction } from 'Douleutaras/constants/system';
import { filterUserProperties } from 'Douleutaras/app/lib/posthog/posthog.config';
import {
  PhoneNumberRevealedProperties,
  ProCardViewedProperties,
  SuccessModalActionProperties,
  SuccessModalShownProperties
} from 'Douleutaras/app/lib/posthog/posthog.types';
import { User } from 'Douleutaras/types';
import { debounce } from 'lodash';
import { EXPERIMENT_VARIANTS } from 'Douleutaras/constants/ab/experiments.const';

let service: PostHogProvider;
let isUserIdentified = false;

interface ABProps {
  experimentKey?: string;
  initializeExperiment?: boolean;
  postHogOptions?: {
    send_event?: boolean;
  };
}

export enum ExtraBidSource {
  JOB_PAGE = 'job_page',
  JOB_PAGE_STICKY_CTA = 'job_page_sticky_cta',
  JOB_PAGE_INLINE_BANNER = 'job_page_inline_banner',
  JOB_PAGE_NO_PROS = 'job_page_no_pros',
  CANCELLATION_MODAL = 'cancellation_modal',
  CCF_NEED_HELP = 'ccf_need_help',
  DUPLICATE_JOB_MODAL = 'job_page_duplicate',
  SUCCESS_MODAL = 'success_modal',
  EMAIL_NO_HANDSHAKE_EXTRA_PRO = 'email_no_handshake_extra_pro',
  VIBER_NO_HANDSHAKE_EXTRA_PRO = 'viber_no_handshake_extra_pro'
}

const DEFAULT_EXPERIMENT_VARIANT = 'test';

const viewedProBuffer: Set<string> = new Set();

export const useAB = ({ experimentKey,
                        initializeExperiment = false,
                        postHogOptions = {} }: ABProps = {}) => {
  const posthog = usePostHog();
  const [ experimentVariant, setExperimentVariant ] = useState<boolean>(false);
  const [ searchParams ] = useSearchParams();
  const abOverrideParam: string | 'control | exp' = searchParams.get('abOverride');
  const location = useLocation();

  const experimentVariants = experimentKey
    ? EXPERIMENT_VARIANTS[experimentKey]
    : { EXPERIMENT_VARIANT_B: DEFAULT_EXPERIMENT_VARIANT };

  // Tracking Plan Events START
  // Referral: https://gitlab.com/d3454/core/tracking-plan/-/blob/main/artifacts/TRACKING_PLAN.md

  const capturePageview = (properties: PostHogCaptureProperties): void => {
    service.capture('$pageview', properties);
  };

  const identifyUser = (id: number, props: User): void => {
    if (isUserIdentified) {
      return;
    }

    isUserIdentified = true;
    service.identify(`${id}`, filterUserProperties(props));
  };

  const captureExtraBid = (source: ExtraBidSource): void => {
    const sourceParam = searchParams.get('source');
    const isKnownSource = Object.values(ExtraBidSource)
      .includes(sourceParam as ExtraBidSource);

    service.capture(PostHogEventNames.EXTRA_PRO_FLOW_TRIGGERED, {
      source: isKnownSource ? (sourceParam as ExtraBidSource) : source
    });
  };

  const captureJobCancelClicked = (jobId, source: ExtraBidSource.JOB_PAGE | ExtraBidSource.CCF_NEED_HELP) => {
    service.capture(PostHogEventNames.JOB_CANCEL_BUTTON_CLICKED, {
      job_id: jobId,
      source
    });
  };

  const captureJobUpdateClicked = (jobId, source: ExtraBidSource.JOB_PAGE) => {
    service.capture(PostHogEventNames.JOB_UPDATE_BUTTON_CLICKED, {
      job_id: jobId,
      source
    });
  };

  const captureNeedHelpTriggered = () => {
    // Behavioral insight event - no properties by design
    service.capture(PostHogEventNames.NEED_HELP_TRIGGERED, undefined, true);
  };

  const captureExpressYourInterestTriggered = () => {
    // Behavioral insight event - no properties by design
    service.capture(PostHogEventNames.EXPRESS_YOUR_INTEREST_TRIGGERED, undefined, true);
  };

  const capturePayOnlineTriggered = () => {
    // Behavioral insight event - no properties by design
    service.capture(PostHogEventNames.PAY_ONLINE_TRIGGERED, undefined, true);
  };

  const captureProCallIntent = (source: PhoneNumberRevealedProperties) => {
    service.capture(PostHogEventNames.PHONE_NUMBER_REVEALED, source);
  };

  const captureSuccessModalShown = (properties: SuccessModalShownProperties) => {
    service.capture(PostHogEventNames.SUCCESS_MODAL_SHOWN, properties);
  };

  const captureSuccessModalAction = (properties: SuccessModalActionProperties) => {
    service.capture(PostHogEventNames.SUCCESS_MODAL_ACTION, properties, true);
  };

  // Internal debounced function to handle aggregated data for "captureProCardViewed"
  const handleAggregatedCapture = debounce((data: ProCardViewedProperties) => {
    if (viewedProBuffer.size === 0) {
      return;
    }

    const aggregatedIds = Array.from(viewedProBuffer);

    service.capture<ProCardViewedProperties>(PostHogEventNames.PRO_CARD_VIEWED, {
      ...data,
      positions: aggregatedIds.filter(item => item.includes('position'))
        .map(item => item.replace('position', '')),
      pro_ids: aggregatedIds.filter(item => item.includes('pro_id'))
        .map(item => item.replace('pro_id', ''))
    });

    viewedProBuffer.clear();
  }, 500);

  const captureProCardViewed = (proId: string, position: number, data: ProCardViewedProperties) => {
    viewedProBuffer.add(`pro_id${proId}`);
    viewedProBuffer.add(`position${position}`);
    handleAggregatedCapture(data);
  };

  const captureJobFormOpened = () => {
    service.capture(PostHogEventNames.JOB_FORM_OPENED, {
      path: window.location.pathname,
      title: document.title
    });
  };

  // Add more methods for tracking here...

  // Tracking Plan Events END

  const fetchExperimentValue = (key: string) => {
    if (!service) {
      // tslint:disable-next-line:no-null-keyword
      return null;
    }

    const variant = service.getFeatureFlag(key, postHogOptions);
    const experimentVariantMatches = Object.values(experimentVariants)
      .includes(variant);

    return !experimentVariant && experimentVariantMatches
      ? variant
      // tslint:disable-next-line:no-null-keyword
      : null;
  };

  const setUpExperiment = (key: string) => {
    if (!key) {
      return;
    }

    const resolvedVariant = fetchExperimentValue(key);

    // tslint:disable-next-line:no-null-keyword
    service.setExperimentContext(key, resolvedVariant);

    // Prevent from double-firing
    // @ts-ignore
    setExperimentVariant(resolvedVariant);
  };

  useEffect(() => {
    service = PostHogProvider.getInstance(posthog, {
      isProduction: isRealProduction,
      isDevelopment
    });

    return () => {
      handleAggregatedCapture.cancel();
      viewedProBuffer.clear();
    };
  }, []);

  useEffect(() => {
    if (experimentKey && initializeExperiment) {
      setUpExperiment(experimentKey);
    } else if (experimentKey) {
      service.setExperimentContext(experimentKey, fetchExperimentValue(experimentKey));
    }
  }, [ experimentKey, initializeExperiment ]);

  const capture = (eventName: PostHogEventNames, properties: PostHogCaptureProperties): void => {
    service.capture(eventName, properties);
  };

  const getExperimentVariant = (): boolean | string => {
    if (Object.values(experimentVariants)
        .includes(abOverrideParam)) {
      return abOverrideParam;
    }

    return experimentVariant;
  };

  return {
    capture,
    identifyUser,
    capturePageview,
    captureExtraBid,
    captureJobCancelClicked,
    captureJobUpdateClicked,
    captureNeedHelpTriggered,
    captureExpressYourInterestTriggered,
    capturePayOnlineTriggered,
    captureProCardViewed,
    captureProCallIntent,
    captureSuccessModalShown,
    captureSuccessModalAction,
    captureJobFormOpened,
    abService: service,
    // If isInProListExperimentGroup passed through the navigation state, it will hold the variant key here
    appliedExperimentVariant: location?.state?.isInProListExperimentGroup || getExperimentVariant()
  };
};
